import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Paper,
} from "@mui/material";
import useSkuPricing from "../../hooks/useSkuPricing";
import useWork from "../../hooks/useWork";
import SkuScopeFilters from "./SkuScopeFilters";

const provinces = [
  "BC",
  "AB",
  "SK",
  "MB",
  "ON",
  "QC",
  "NB",
  "NS",
  "NL",
  "PE",
  "NT",
  "YT",
  "NU",
];

const formatCAD = (value) => {
  if (!value && value !== 0) return "-";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(value);
};

const SkuPricingView = ({ sku, clientId }) => {
  const { client, pricingData, loading, fetchPricing } = useSkuPricing();
  const { queueWork } = useWork();

  const [filters, setFilters] = useState({});

  useEffect(() => {
    if (clientId && sku?.id) {
      fetchPricing(clientId, sku.id, filters);
    }
  }, [clientId, sku?.id, filters]);

  const handleExportTemplate = async () => {
    const email = prompt("Enter email address for template delivery:");
    if (!email) return;

    await queueWork({
      workerType: "exportSkuPricingTemplate",
      details: { email, entityId: clientId },
    });

    alert("Pricing template export queued. Check your email.");
  };

  if (!client) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">
          Loading client configuration...
        </Typography>
      </Box>
    );
  }

  if (!client.pricingOptions) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">
          No pricing options configured for this client.
        </Typography>
      </Box>
    );
  }

  const { retailLevel, countryLevel } = client.pricingOptions;
  const isProvincial = countryLevel === "provincial";

  const renderTable = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (pricingData.length === 0) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="text.secondary">
            No pricing configured for this SKU. Export template to add pricing.
          </Typography>
        </Box>
      );
    }

    return (
      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {retailLevel !== "national" && <TableCell>Scope</TableCell>}
              {isProvincial ? (
                provinces.map((p) => <TableCell key={p}>{p}</TableCell>)
              ) : (
                <TableCell>Price</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {pricingData.map((pricing) => (
              <TableRow key={pricing.id}>
                {retailLevel !== "national" && (
                  <TableCell>
                    {pricing.retailerName ||
                      pricing.bannerName ||
                      pricing.locationName ||
                      "N/A"}
                  </TableCell>
                )}
                {isProvincial ? (
                  provinces.map((p) => (
                    <TableCell key={p}>
                      {formatCAD(pricing.pricing?.prices?.[p])}
                    </TableCell>
                  ))
                ) : (
                  <TableCell>
                    {formatCAD(pricing.pricing?.prices?.national)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  return (
    <Box>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1 }}
      >
        Client: {client.name} ({retailLevel} / {countryLevel})
      </Typography>

      <SkuScopeFilters
        retailLevel={retailLevel}
        countryLevel={countryLevel}
        clientId={clientId}
        onChange={setFilters}
      />

      {renderTable()}

      <Box sx={{ mt: 2, display: "flex", gap: 2, alignItems: "center" }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleExportTemplate}
        >
          Export Pricing Template
        </Button>
        <Typography
          variant="caption"
          color="text.secondary"
        >
          Bulk edits via export → Excel → import
        </Typography>
      </Box>
    </Box>
  );
};

export default SkuPricingView;
